import { jsPDF } from "jspdf";

// Charge une image telle quelle (data URL) — pour le logo transparent
const loadRawImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ data: c.toDataURL("image/png"), ratio: img.height / img.width });
    };
    img.onerror = reject;
    img.src = src;
  });

// Charge un portrait, le découpe en cercle et l'entoure d'un halo crème
// en dégradé (adoucit le bord à l'impression) — retourne { data, pad }
const loadCirclePortrait = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(img.width, img.height);
      const pad = 1.22; // marge autour du cercle pour le halo
      const S = Math.round(s * pad);
      const c = document.createElement("canvas");
      c.width = c.height = S;
      const ctx = c.getContext("2d");
      // Halo crème doux qui s'estompe vers le fond de la carte
      const grad = ctx.createRadialGradient(S / 2, S / 2, s * 0.3, S / 2, S / 2, S / 2);
      grad.addColorStop(0, "rgba(248,243,232,0.9)");
      grad.addColorStop(0.8, "rgba(248,243,232,0.5)");
      grad.addColorStop(1, "rgba(248,243,232,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, S, S);
      // Portrait circulaire par-dessus
      ctx.save();
      ctx.beginPath();
      ctx.arc(S / 2, S / 2, s / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, (img.width - s) / 2, 0, s, s, (S - s) / 2, (S - s) / 2, s, s);
      ctx.restore();
      resolve({ data: c.toDataURL("image/png"), pad });
    };
    img.onerror = reject;
    img.src = src;
  });

// Carte imprimable A5 du Gardien — à glisser dans un cadeau
export const downloadGardienCard = async (g) => {
  const doc = new jsPDF({ unit: "mm", format: "a5" }); // 148 × 210
  const W = 148;
  const INK = [61, 44, 30];
  const GOLD = [176, 141, 87];
  const SOFT = [122, 92, 62];

  // Fond crème + double liseré doré
  doc.setFillColor(248, 243, 232);
  doc.rect(0, 0, W, 210, "F");
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.9);
  doc.rect(6, 6, W - 12, 198);
  doc.setLineWidth(0.3);
  doc.rect(8.5, 8.5, W - 17, 193);

  // En-tête : logo Passerelle détouré
  let headerBottom = 20;
  try {
    const logo = await loadRawImage("/logo-pont.png");
    const lw = 30;
    doc.addImage(logo.data, "PNG", W / 2 - lw / 2, 11, lw, lw * logo.ratio);
    headerBottom = 11 + lw * logo.ratio;
  } catch {
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...INK);
    doc.text("Passerelle", W / 2, 21, { align: "center" });
    headerBottom = 24;
  }
  doc.setFont("times", "italic");
  doc.setFontSize(9.5);
  doc.setTextColor(...SOFT);
  doc.text("Les Gardiens des Souvenirs et des forces intérieures", W / 2, headerBottom + 5, { align: "center" });

  // Portrait (avec halo crème : la boîte image est plus grande que le cercle visible)
  try {
    const portrait = await loadCirclePortrait(g.image);
    const box = 48 * portrait.pad;
    doc.addImage(portrait.data, "PNG", W / 2 - box / 2, headerBottom + 11 - (box - 48) / 2, box, box);
  } catch {
    /* la carte reste belle même sans portrait */
  }

  // Identité du Gardien
  const yId = headerBottom + 11 + 48; // bas du portrait
  doc.setFont("times", "bold");
  doc.setFontSize(25);
  doc.setTextColor(...INK);
  doc.text(g.name, W / 2, yId + 9, { align: "center" });
  doc.setFont("times", "italic");
  doc.setFontSize(12);
  doc.setTextColor(...SOFT);
  doc.text(g.title, W / 2, yId + 16.5, { align: "center" });
  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...GOLD);
  doc.text(g.value.toUpperCase(), W / 2, yId + 23.5, { align: "center" });

  // Citation
  doc.setFont("times", "italic");
  doc.setFontSize(12.5);
  doc.setTextColor(...INK);
  const quote = doc.splitTextToSize(`« ${g.quote} »`, 108);
  doc.text(quote, W / 2, yId + 30, { align: "center" });

  // Pathologie & thérapie douce
  const careY = yId + 30 + quote.length * 5.2 + 3;
  doc.setFontSize(9);
  doc.setTextColor(...SOFT);
  doc.setFont("times", "normal");
  doc.text(`Pathologie : ${g.pathologie}`, W / 2, careY, { align: "center" });
  doc.setFont("times", "italic");
  doc.text(`Thérapie : ${g.therapie}`, W / 2, careY + 4.5, { align: "center" });

  // Séparateur
  const sepY = careY + 11;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(W / 2 - 24, sepY, W / 2 + 24, sepY);

  // Activités
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text("Trois douceurs à vivre ensemble", W / 2, sepY + 8, { align: "center" });
  doc.setFont("times", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(70, 58, 46);
  let y = sepY + 15;
  g.activities.forEach((a) => {
    const lines = doc.splitTextToSize(a, 108);
    doc.text("•", 20, y);
    doc.text(lines, 24, y);
    y += lines.length * 4.2 + 2;
  });

  // Fidèle compagnon (Gardiens des Souvenirs)
  if (g.familiar) {
    const ligne = `et son fidèle compagnon ${g.familiar.name}, ${g.familiar.title}`;
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    const lw = doc.getTextWidth(ligne);
    try {
      const fimg = await loadCirclePortrait(g.familiar.image);
      const fb = 11 * fimg.pad;
      doc.addImage(fimg.data, "PNG", (W - lw) / 2 - 15 - (fb - 11) / 2, y + 1.5 - (fb - 11) / 2, fb, fb);
    } catch { /* texte seul si l'image manque */ }
    doc.setTextColor(...SOFT);
    doc.text(ligne, W / 2 + 6, y + 8.5, { align: "center" });
  }

  // Pied de carte
  doc.setFont("times", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(...SOFT);
  doc.text("Carte découverte grâce au quiz des Gardiens — passerelle.site", W / 2, 197, { align: "center" });

  doc.save(`carte-gardien-${g.id}.pdf`);
};

// Carte imprimable A5 du fidèle compagnon d'un Gardien
export const downloadFamilierCard = async (g) => {
  if (!g.familiar) return;
  const f = g.familiar;
  const doc = new jsPDF({ unit: "mm", format: "a5" });
  const W = 148;
  const INK = [61, 44, 30];
  const GOLD = [176, 141, 87];
  const SOFT = [122, 92, 62];

  doc.setFillColor(248, 243, 232);
  doc.rect(0, 0, W, 210, "F");
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.9);
  doc.rect(6, 6, W - 12, 198);
  doc.setLineWidth(0.3);
  doc.rect(8.5, 8.5, W - 17, 193);

  // En-tête : logo Passerelle détouré
  let headerBottom = 20;
  try {
    const logo = await loadRawImage("/logo-pont.png");
    const lw = 30;
    doc.addImage(logo.data, "PNG", W / 2 - lw / 2, 11, lw, lw * logo.ratio);
    headerBottom = 11 + lw * logo.ratio;
  } catch {
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...INK);
    doc.text("Passerelle", W / 2, 21, { align: "center" });
    headerBottom = 24;
  }
  doc.setFont("times", "italic");
  doc.setFontSize(9.5);
  doc.setTextColor(...SOFT);
  doc.text("Les Fidèles Compagnons des Gardiens", W / 2, headerBottom + 5, { align: "center" });

  try {
    const portrait = await loadCirclePortrait(f.image);
    const box = 56 * portrait.pad;
    doc.addImage(portrait.data, "PNG", W / 2 - box / 2, headerBottom + 10 - (box - 56) / 2, box, box);
  } catch { /* la carte reste belle même sans portrait */ }

  const yId = headerBottom + 10 + 56; // bas du portrait
  doc.setFont("times", "bold");
  doc.setFontSize(25);
  doc.setTextColor(...INK);
  doc.text(f.name, W / 2, yId + 10, { align: "center" });
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(...SOFT);
  doc.text(f.title, W / 2, yId + 18, { align: "center" });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(W / 2 - 24, yId + 26, W / 2 + 24, yId + 26);

  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  const prep = /^[AÀEÉÈEIOU]/.test(g.name) ? "d'" : "de ";
  const lien = doc.splitTextToSize(`Fidèle compagnon ${prep}${g.name}, ${g.title}`, 108);
  doc.text(lien, W / 2, yId + 36, { align: "center" });

  doc.setFont("times", "italic");
  doc.setFontSize(11.5);
  doc.setTextColor(...SOFT);
  const histoire = doc.splitTextToSize(f.story || `« ${g.quote} »`, 100);
  doc.text(histoire, W / 2, yId + 36 + lien.length * 5 + 8, { align: "center" });

  doc.setFontSize(8.5);
  doc.text("Les Gardiens des Souvenirs et des forces intérieures — passerelle.site", W / 2, 197, { align: "center" });

  doc.save(`carte-familier-${g.id}.pdf`);
};
